import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';

import styles from './Chat.module.css';
import ChatUi from '../../components/UI/Common/ChatUi/ChatUi';
import { getAllChatroom } from '../../services/chatService';
import io from "socket.io-client"; // Importing socket.io for real-time chat
import { useNavigate } from 'react-router-dom';

// Create socket instance outside component to prevent recreation on rerenders
const socketInstance = io('https://findcv.com', {
    transports: ['websocket', 'polling'], // Try websocket first, then fall back to polling
    reconnection: true,
    secure:true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
});
// const socketInstance = io('http://localhost:3080', {
//     transports: ['websocket', 'polling'], // Try websocket first, then fall back to polling
//     reconnection: true,
//     reconnectionAttempts: 5,
//     reconnectionDelay: 1000,
// });
 
// const socketInstance = io("https://findcv.com", {
//     path: "/socket.io/", // Explicitly match the server's path
//     transports: ["polling", "websocket"], // Allow fallback to polling
// });


function Chat() {
    const [recievedMsg, setrecievedMsg] = useState({});
    const [contacts, setContacts] = useState([]);
    const user = useSelector((state) => state.user.user);
    const navigate = useNavigate();

    // Sort contacts with the most recent message at the top
    const sortContactsByLatestMessage = useCallback((contactsToSort) => {
        return [...contactsToSort].sort((a, b) => {
            // Compare lastMessageTime if available
            const timeA = a.lastMessageTime ? new Date(a.lastMessageTime).getTime() : 0;
            const timeB = b.lastMessageTime ? new Date(b.lastMessageTime).getTime() : 0;
            
            // Sort in descending order (newest first)
            return timeB - timeA;
        });
    }, []);

    // Memoize the fetch chatroom function to prevent recreation on each render
    const fetchChatroom = useCallback(async () => {
        if (!user?.id || !user?.account_type) return;
        
        try {
            const getAllChatroomResponse = await getAllChatroom(user.account_type, user.id);
            const getAllChatroomResponseCopy = JSON.parse(JSON.stringify(getAllChatroomResponse));
            const transformedContacts = getAllChatroomResponseCopy.map(chatroom => {
                return ({
                    id: chatroom?.chatroom?.id || chatroom?.id,
                    name: (chatroom.first_name && chatroom.last_name)
                        ? `${chatroom.first_name} ${chatroom.last_name}`
                        : chatroom.email?.split('@')[0],
                    avatar: chatroom?.profile_image,
                    lastMessage: chatroom?.lastMessage?.messageContent || 'no message',
                    lastMessageTime: chatroom?.lastMessage?.createdAt || null, // Store message timestamp
                    chatroom: { ...chatroom },
                });
            });
            
            // Sort contacts with most recent message at the top
            const sortedContacts = sortContactsByLatestMessage(transformedContacts);
            setContacts(sortedContacts);
        } catch (error) {
            console.error('Error fetching chatrooms:', error);
        }
    }, [user?.account_type, user?.id, sortContactsByLatestMessage]);

    // Update the latest message in a chatroom and reorder contacts
    const updateLatestMessage = useCallback((message) => {
        if (!message || !message.roomId) return;
        
        setContacts(prevContacts => {
            // First update the last message for the relevant chatroom
            const updatedContacts = prevContacts.map(contact => {
                if (contact.id === message.roomId) {
                    // Update the last message for this chatroom
                    return {
                        ...contact,
                        lastMessage: message.message || message.messageContent,
                        lastMessageTime: message.timestamp || new Date().toISOString(), // Update timestamp
                    };
                }
                return contact;
            });
            
            // Then sort contacts to put the one with new message at the top
            return sortContactsByLatestMessage(updatedContacts);
        });
    }, [sortContactsByLatestMessage]);

    // Socket connection and event handlers
    useEffect(() => {
        socketInstance.on('connection', () => {
            console.log('Connected');
        });

        socketInstance.on('connect_error', (err) => {
            console.error('Connection Error:', err);
        });

        // Handle receiving messages
        socketInstance.on('receiveMessage', (message) => {
            // Store a reference to the received message
            setrecievedMsg(message);
            
            // Update the latest message in the chatroom list
            updateLatestMessage(message);
            
            // Only refresh contacts list if we received a message from someone else
            // This prevents unnecessary API calls when sending messages ourselves
            if (message.senderId !== user?.id) {
                fetchChatroom();
            }
        });

        socketInstance.on('disconnect', () => {
            console.log('Disconnected from Socket.IO');
        });

        // Fetch chatrooms on component mount
        fetchChatroom();

        // Cleanup on unmount
        return () => {
            socketInstance.off('connection');
            socketInstance.off('connect_error');
            socketInstance.off('receiveMessage');
            socketInstance.off('disconnect');
            // Don't disconnect socket as it may be used elsewhere
            // socketInstance.disconnect();
        };
    }, [fetchChatroom, updateLatestMessage, user?.id]);

    // Handle when user sends a new message
    const handleMessageSent = useCallback((message) => {
        updateLatestMessage(message);
    }, [updateLatestMessage]);

    return (
        <div className={styles.wrapper}>
            <Helmet>
                <title>FindCV - Chat</title>
            </Helmet>
            <ChatUi 
                contacts={contacts} 
                setContacts={setContacts} 
                recievedMsg={recievedMsg} 
                socketInstance={socketInstance} 
                onMessageSent={handleMessageSent}
            />
        </div>
    );
}

export default Chat;